<template>
  <div class="user-info-container">
    <div class="content-wrapper">
      <div class="user-info-header">
        <h1 class="page-title">{{ systemStatus.site_name }} 用户中心</h1>
        <p class="page-subtitle">账户信息与服务管理</p>
      </div>
      
      <v-container class="transparent-container">
      <div v-if="loading" class="loading-container">
        <div class="loading-content">
          <v-progress-circular 
            indeterminate 
            color="primary" 
            size="50"
            width="4"
          ></v-progress-circular>
          <div class="loading-text">加载中...</div>
        </div>
      </div>

      <div v-else-if="error" class="error-container">
        <v-alert 
          type="error" 
          class="error-alert"
          rounded="lg"
          elevation="4"
        >
          {{ error }}
        </v-alert>
      </div>

      <div v-else>
        <v-card class="user-info-card mb-4">
          <v-card-title class="card-title-section">
            <v-icon start color="primary">mdi-account-circle</v-icon> 个人信息
          </v-card-title>
          <v-card-text>
            <div class="d-flex justify-space-between mb-3 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="primary" class="mr-2">mdi-star-circle</v-icon>
                <span>可用积分：</span>
              </div>
              <div class="d-flex">
                <v-btn
                  icon
                  size="x-small"
                  color="amber-darken-2"
                  variant="outlined"
                  @click="openCreditsStoreDialog"
                  title="积分商城"
                  class="mr-2 credits-store-btn"
                >
                  <v-icon size="small">mdi-store</v-icon>
                </v-btn>
                <div class="value-display credits-value">{{ userInfo.credits.toFixed(2) }}</div>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-3 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="success" class="mr-2">mdi-currency-usd</v-icon>
                <span>捐赠金额：</span>
              </div>
              <div class="d-flex align-center">
                <v-btn
                  icon
                  size="x-small"
                  color="amber-darken-2"
                  variant="outlined"
                  @click="openDonationManagementDialog"
                  title="捐赠管理"
                  class="mr-2 donation-manage-btn"
                >
                  <v-icon size="small">mdi-cog</v-icon>
                </v-btn>
                <div class="value-display donation-value">{{ userInfo.donation.toFixed(2) }}</div>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-3 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="purple-darken-1" class="mr-2">mdi-account-multiple</v-icon>
                <span>邀请人数：</span>
              </div>
              <div class="value-display invitee-value">{{ userInfo.invitee_count || 0 }}</div>
            </div>

            <!-- 用户勋章展示 -->
            <div v-if="!badgesLoading && userBadges.length > 0" class="d-flex justify-space-between mb-3 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-medal</v-icon>
                <span>我的勋章：</span>
              </div>
              <div class="d-flex align-center gap-1">
                <v-tooltip
                  v-for="badge in userBadges"
                  :key="badge.badge_id"
                  location="top"
                  open-on-click
                  open-on-hover
                >
                  <template v-slot:activator="{ props }">
                    <v-avatar
                      v-bind="props"
                      size="28"
                      class="badge-icon"
                      :class="{ 'badge-expired': !badge.bonus_active }"
                    >
                      <v-img :src="badge.badge.icon_url" :alt="badge.badge.name" />
                    </v-avatar>
                  </template>
                  <div class="badge-tooltip">
                    <div class="font-weight-bold mb-1">{{ badge.badge.name }}</div>
                    <div class="text-caption">加成: +{{ (badge.badge.bonus_percentage * 100).toFixed(0) }}%</div>
                    <div class="text-caption" :class="badge.bonus_active ? '' : 'text-warning'">
                      加成{{ badge.bonus_active ? '有效至' : '已过期' }}: {{ formatBadgeExpireDate(badge.expires_at) }}
                    </div>
                  </div>
                </v-tooltip>
              </div>
            </div>
            <div v-else-if="badgesLoading" class="d-flex justify-space-between mb-3 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-medal</v-icon>
                <span>我的勋章：</span>
              </div>
              <div class="d-flex align-center">
                <v-progress-circular indeterminate size="20" width="2" color="amber"></v-progress-circular>
              </div>
            </div>

            <v-divider class="my-3"></v-divider>

            <div v-if="userInfo.invitation_codes && userInfo.invitation_codes.length > 0">
              <div class="font-weight-bold mb-2 d-flex align-center">
                <v-icon size="small" color="info" class="mr-2">mdi-ticket-account</v-icon>
                <span>可用邀请码：</span>
              </div>
              <div v-for="(code, index) in userInfo.invitation_codes" :key="index" class="mb-2">
                <div class="invitation-code-row-horizontal">
                  <div class="invitation-code-container-horizontal">
                    <div class="invitation-code-with-tag">
                      <v-chip 
                        size="small" 
                        :color="privilegedCodes[index] ? 'amber-darken-2' : 'primary'"
                        @click="copyToClipboard(code)"
                        class="invitation-chip-horizontal"
                        elevation="2"
                        rounded="lg"
                        :title="code"
                      >
                        <span class="code-text">{{ code.length > 16 ? code.substring(0, 10) + '...' + code.substring(code.length - 4) : code }}</span>
                        <v-icon end icon="mdi-content-copy" size="small"></v-icon>
                      </v-chip>
                      <!-- 特权码提示文字 - 放在邀请码右侧 -->
                      <div v-if="privilegedCodes[index]" class="privilege-tag-horizontal">
                        <v-icon size="x-small" color="amber-darken-2">mdi-crown</v-icon>
                        <span class="privilege-text">特权</span>
                      </div>
                    </div>
                  </div>
                  <v-btn
                    size="x-small"
                    color="success"
                    variant="outlined"
                    @click="redeemCodeForCredits(code, index)"
                    :loading="redeemingCodes[index]"
                    :disabled="redeemingCodes[index]"
                    class="redeem-button-horizontal"
                    title="兑换为积分"
                  >
                    <v-icon size="small" start>mdi-star</v-icon>
                    兑换积分
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-subtitle-2 my-2">
              <v-icon color="grey" class="mr-1">mdi-ticket-confirmation-outline</v-icon>
              暂无可用邀请码
            </div>
          </v-card-text>
        </v-card>

        <!-- Plex 账户信息 -->
        <v-card v-if="userInfo.plex_info" class="user-info-card mb-4">
          <v-card-title class="card-title-section">
            <v-icon start color="orange-darken-2">mdi-plex</v-icon> Plex 账户
          </v-card-title>
          <v-card-text>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="blue-darken-1" class="mr-2">mdi-account</v-icon>
                <span>用户名：</span>
              </div>
              <div class="d-flex align-center justify-end">
                {{ userInfo.plex_info.username }}
                <v-icon 
                  v-if="userInfo.plex_info.is_premium" 
                  size="small" 
                  color="amber-darken-2" 
                  class="ml-1" 
                  title="会员用户"
                >
                  mdi-crown
                </v-icon>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="red-darken-1" class="mr-2">mdi-email</v-icon>
                <span>邮箱：</span>
              </div>
              <div>{{ userInfo.plex_info.email }}</div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="purple-darken-1" class="mr-2">mdi-clock-time-four-outline</v-icon>
                <span>观看等级：</span>
              </div>
              <div class="d-flex align-center justify-end" :title="`观看时长: ${userInfo.plex_info.watched_time.toFixed(2)}小时`">
                <template v-if="watchLevelIcons(userInfo.plex_info.watched_time).length > 0">
                  <div class="level-icons-container" @click="showWatchTimeDialog(userInfo.plex_info.watched_time)">
                    <span 
                      v-for="(icon, index) in watchLevelIcons(userInfo.plex_info.watched_time)" 
                      :key="`plex-icon-${index}`"
                      :class="['emoji-icon', icon.class]"
                    >
                      {{ icon.icon }}
                    </span>
                  </div>
                </template>
                <span v-else-if="showNoWatchTimeText(userInfo.plex_info.watched_time)" class="text-grey">暂无观看记录</span>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="indigo-darken-1" class="mr-2">mdi-folder-multiple</v-icon>
                <span>资料库权限：</span>
              </div>
              <v-chip 
                :color="userInfo.plex_info.all_lib ? 'success' : 'warning'" 
                size="small"
                @click="openNsfwDialog('plex', userInfo.plex_info.all_lib)"
                class="clickable-chip"
                elevation="1"
              >
                {{ userInfo.plex_info.all_lib ? '全部' : '部分' }}
                <v-icon end size="x-small" class="ml-1">mdi-pencil</v-icon>
              </v-chip>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="light-blue-darken-1" class="mr-2">mdi-download</v-icon>
                <span>下载权限：</span>
              </div>
              <v-chip 
                :color="userInfo.plex_info.download_unlocked ? 'success' : 'grey'" 
                size="small"
                @click="openDownloadUnlockDialog('plex')"
                class="clickable-chip"
                elevation="1"
              >
                <v-icon start size="x-small">{{ userInfo.plex_info.download_unlocked ? 'mdi-check' : 'mdi-lock' }}</v-icon>
                {{ userInfo.plex_info.download_unlocked ? '已解锁' : '未解锁' }}
                <v-icon v-if="!userInfo.plex_info.download_unlocked" end size="x-small" class="ml-1">mdi-pencil</v-icon>
              </v-chip>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="teal-darken-1" class="mr-2">mdi-connection</v-icon>
                <span>绑定线路：</span>
              </div>
              <div class="d-flex align-center gap-2">
                <plex-line-selector 
                  ref="plexLineSelector"
                  :current-value="userInfo.plex_info.line" 
                  @line-changed="updatePlexLine"
                ></plex-line-selector>
                <v-btn
                  size="small"
                  color="purple"
                  variant="tonal"
                  icon
                  @click="openPlexScheduleDialog"
                  title="高级调度"
                >
                  <v-icon size="small">mdi-calendar-clock</v-icon>
                </v-btn>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="blue-grey-darken-1" class="mr-2">mdi-cloud-download</v-icon>
                <span>今日总流量：</span>
              </div>
              <div class="value-display traffic-value">{{ formatTraffic(userInfo.plex_info.daily_traffic || 0) }}</div>
            </div>
            <div v-if="userInfo.plex_info.daily_premium_traffic > 0" class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-star-circle</v-icon>
                <span>今日 Premium 流量：</span>
              </div>
              <div class="value-display traffic-value premium-traffic">{{ formatTraffic(userInfo.plex_info.daily_premium_traffic || 0) }}</div>
            </div>
            <div v-if="systemStatus.premium_free || userInfo.plex_info.is_premium || userInfo.plex_info.daily_premium_debt > 0 || userInfo.plex_info.daily_premium_projected_debt > 0" class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="green-darken-1" class="mr-2">mdi-gauge</v-icon>
                <span>Premium 日基础余量：</span>
              </div>
              <div class="d-flex align-center">
                <v-tooltip location="top" open-on-click open-on-hover>
                  <template v-slot:activator="{ props }">
                    <div v-bind="props" class="value-display traffic-value free-premium-remaining quota-trigger">{{ formatTraffic(userInfo.plex_info.daily_premium_free_remaining || 0) }}</div>
                  </template>
                  <div class="quota-tooltip">
                    <div>基础日额：{{ formatTraffic(userInfo.plex_info.daily_premium_free_limit || 0) }}</div>
                    <div>当前欠额：{{ formatTraffic(userInfo.plex_info.daily_premium_debt || 0) }}</div>
                    <div>预计结算后欠额：{{ formatTraffic(userInfo.plex_info.daily_premium_projected_debt || 0) }}</div>
                  </div>
                </v-tooltip>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-crown</v-icon>
                <span>Premium 会员：</span>
              </div>
              <v-btn
                size="small"
                :color="userInfo.plex_info.is_premium ? 'success' : 'blue-grey-lighten-1'"
                :variant="userInfo.plex_info.is_premium ? 'flat' : 'tonal'"
                @click="openPremiumUnlockDialog('plex')"
                :title="!systemStatus.premium_unlock_enabled ? 'Premium 解锁功能暂未开放' : (userInfo.plex_info.is_premium ? '续费 Premium' : '解锁 Premium')"
                :disabled="!systemStatus.premium_unlock_enabled"
                elevation="2"
                rounded="xl"
                class="premium-button"
                :class="{ 'premium-active': userInfo.plex_info.is_premium }"
              >
                <v-icon start size="small">{{ userInfo.plex_info.is_premium ? 'mdi-crown' : 'mdi-crown-outline' }}</v-icon>
                {{ userInfo.plex_info.is_premium ? '已激活 - 续费' : '未激活 - 解锁' }}
              </v-btn>
            </div>
            <div v-if="userInfo.plex_info.is_premium" class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="deep-orange-darken-1" class="mr-2">mdi-clock-outline</v-icon>
                <span>Premium 到期时间：</span>
              </div>
              <div class="text-caption" :class="isPremiumExpiringSoon(userInfo.plex_info.premium_expiry) ? 'text-warning' : ''">
                {{ formatPremiumExpiry(userInfo.plex_info.premium_expiry) }}
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="cyan-darken-1" class="mr-2">mdi-clock-time-eight-outline</v-icon>
                <span>最后观看时间：</span>
              </div>
              <div class="text-caption">
                {{ formatLastViewedAt(userInfo.plex_info.last_viewed_at) }}
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Emby 账户信息 -->
        <v-card v-if="userInfo.emby_info" class="user-info-card mb-4">
          <v-card-title class="card-title-section">
            <v-icon start color="green-darken-2">mdi-emby</v-icon> Emby 账户
          </v-card-title>
          <v-card-text>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="blue-darken-1" class="mr-2">mdi-account</v-icon>
                <span>用户名：</span>
              </div>
              <div class="d-flex align-center justify-end">
                {{ userInfo.emby_info.username }}
                <v-icon 
                  v-if="userInfo.emby_info.is_premium" 
                  size="small" 
                  color="amber-darken-2" 
                  class="ml-1" 
                  title="会员用户"
                >
                  mdi-crown
                </v-icon>
              </div>
            </div>
            <div v-if="userInfo.emby_info.created_at" class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="pink-darken-1" class="mr-2">mdi-cake-variant</v-icon>
                <span>破壳日：</span>
              </div>
              <div>{{ userInfo.emby_info.created_at }}</div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="purple-darken-1" class="mr-2">mdi-clock-time-four-outline</v-icon>
                <span>观看等级：</span>
              </div>
              <div class="d-flex align-center justify-end" :title="`观看时长: ${userInfo.emby_info.watched_time.toFixed(2)}小时`">
                <template v-if="watchLevelIcons(userInfo.emby_info.watched_time).length > 0">
                  <div class="level-icons-container" @click="showWatchTimeDialog(userInfo.emby_info.watched_time)">
                    <span 
                      v-for="(icon, index) in watchLevelIcons(userInfo.emby_info.watched_time)" 
                      :key="`emby-icon-${index}`"
                      :class="['emoji-icon', icon.class]"
                    >
                      {{ icon.icon }}
                    </span>
                  </div>
                </template>
                <span v-else-if="showNoWatchTimeText(userInfo.emby_info.watched_time)" class="text-grey">暂无观看记录</span>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="indigo-darken-1" class="mr-2">mdi-folder-multiple</v-icon>
                <span>资料库权限：</span>
              </div>
              <v-chip 
                :color="userInfo.emby_info.all_lib ? 'success' : 'warning'" 
                size="small"
                @click="openNsfwDialog('emby', userInfo.emby_info.all_lib)"
                class="clickable-chip"
                elevation="1"
              >
                {{ userInfo.emby_info.all_lib ? '全部' : '部分' }}
                <v-icon end size="x-small" class="ml-1">mdi-pencil</v-icon>
              </v-chip>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="light-blue-darken-1" class="mr-2">mdi-download</v-icon>
                <span>下载权限：</span>
              </div>
              <v-chip 
                :color="userInfo.emby_info.download_unlocked ? 'success' : 'grey'" 
                size="small"
                @click="openDownloadUnlockDialog('emby')"
                class="clickable-chip"
                elevation="1"
              >
                <v-icon start size="x-small">{{ userInfo.emby_info.download_unlocked ? 'mdi-check' : 'mdi-lock' }}</v-icon>
                {{ userInfo.emby_info.download_unlocked ? '已解锁' : '未解锁' }}
                <v-icon v-if="!userInfo.emby_info.download_unlocked" end size="x-small" class="ml-1">mdi-pencil</v-icon>
              </v-chip>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center entrance-url-row">
              <div class="d-flex align-center">
                <v-icon size="small" color="deep-orange-darken-1" class="mr-2">mdi-web</v-icon>
                <span>入口线路：</span>
              </div>
              <v-btn
                size="x-small"
                color="deep-orange-darken-1"
                variant="outlined"
                class="entrance-copy-btn"
                :disabled="!systemStatus.emby_entry_url"
                @click="copyToClipboard(systemStatus.emby_entry_url)"
                title="点击复制线路地址"
              >
                <v-icon start size="small">mdi-content-copy</v-icon>
                复制链接
              </v-btn>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="teal-darken-1" class="mr-2">mdi-connection</v-icon>
                <span>绑定线路：</span>
              </div>
              <div class="d-flex align-center gap-2">
                <emby-line-selector 
                  ref="embyLineSelector"
                  :current-value="userInfo.emby_info.line" 
                  @line-changed="updateEmbyLine"
                ></emby-line-selector>
                <v-btn
                  size="small"
                  color="purple"
                  variant="tonal"
                  icon
                  @click="openEmbyScheduleDialog"
                  title="高级调度"
                >
                  <v-icon size="small">mdi-calendar-clock</v-icon>
                </v-btn>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="blue-grey-darken-1" class="mr-2">mdi-cloud-download</v-icon>
                <span>今日总流量：</span>
              </div>
              <div class="value-display traffic-value">{{ formatTraffic(userInfo.emby_info.daily_traffic || 0) }}</div>
            </div>
            <div v-if="userInfo.emby_info.daily_premium_traffic > 0" class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-star-circle</v-icon>
                <span>今日 Premium 流量：</span>
              </div>
              <div class="value-display traffic-value premium-traffic">{{ formatTraffic(userInfo.emby_info.daily_premium_traffic || 0) }}</div>
            </div>
            <div v-if="systemStatus.premium_free || userInfo.emby_info.is_premium || userInfo.emby_info.daily_premium_debt > 0 || userInfo.emby_info.daily_premium_projected_debt > 0" class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="green-darken-1" class="mr-2">mdi-gauge</v-icon>
                <span>Premium 日基础余量：</span>
              </div>
              <div class="d-flex align-center">
                <v-tooltip location="top" open-on-click open-on-hover>
                  <template v-slot:activator="{ props }">
                    <div v-bind="props" class="value-display traffic-value free-premium-remaining quota-trigger">{{ formatTraffic(userInfo.emby_info.daily_premium_free_remaining || 0) }}</div>
                  </template>
                  <div class="quota-tooltip">
                    <div>基础日额：{{ formatTraffic(userInfo.emby_info.daily_premium_free_limit || 0) }}</div>
                    <div>当前欠额：{{ formatTraffic(userInfo.emby_info.daily_premium_debt || 0) }}</div>
                    <div>预计结算后欠额：{{ formatTraffic(userInfo.emby_info.daily_premium_projected_debt || 0) }}</div>
                  </div>
                </v-tooltip>
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-crown</v-icon>
                <span>Premium 会员：</span>
              </div>
              <v-btn
                size="small"
                :color="userInfo.emby_info.is_premium ? 'success' : 'blue-grey-lighten-1'"
                :variant="userInfo.emby_info.is_premium ? 'flat' : 'tonal'"
                @click="openPremiumUnlockDialog('emby')"
                :title="!systemStatus.premium_unlock_enabled ? 'Premium 解锁功能暂未开放' : (userInfo.emby_info.is_premium ? '续费 Premium' : '解锁 Premium')"
                :disabled="!systemStatus.premium_unlock_enabled"
                elevation="2"
                rounded="xl"
                class="premium-button"
                :class="{ 'premium-active': userInfo.emby_info.is_premium }"
              >
                <v-icon start size="small">{{ userInfo.emby_info.is_premium ? 'mdi-crown' : 'mdi-crown-outline' }}</v-icon>
                {{ userInfo.emby_info.is_premium ? '已激活 - 续费' : '未激活 - 解锁' }}
              </v-btn>
            </div>
            <div v-if="userInfo.emby_info.is_premium" class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="deep-orange-darken-1" class="mr-2">mdi-clock-outline</v-icon>
                <span>Premium 到期时间：</span>
              </div>
              <div class="text-caption" :class="isPremiumExpiringSoon(userInfo.emby_info.premium_expiry) ? 'text-warning' : ''">
                {{ formatPremiumExpiry(userInfo.emby_info.premium_expiry) }}
              </div>
            </div>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="cyan-darken-1" class="mr-2">mdi-clock-time-eight-outline</v-icon>
                <span>最后观看时间：</span>
              </div>
              <div class="text-caption">
                {{ formatLastViewedAt(userInfo.emby_info.last_viewed_at) }}
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Overseerr 账户信息 -->
        <v-card v-if="userInfo.overseerr_info" class="user-info-card mb-4">
          <v-card-title class="card-title-section">
            <v-icon start color="blue-darken-2">mdi-movie-search</v-icon> Overseerr 账户
          </v-card-title>
          <v-card-text>
            <div class="d-flex justify-space-between mb-2 align-center">
              <div class="d-flex align-center">
                <v-icon size="small" color="red-darken-1" class="mr-2">mdi-email</v-icon>
                <span>邮箱：</span>
              </div>
              <div>{{ userInfo.overseerr_info.email }}</div>
            </div>
          </v-card-text>
        </v-card>

        <!-- 个人活动数据卡片 -->
        <v-card class="user-info-card mb-4">
          <v-card-title class="card-title-section">
            <v-icon start color="deep-purple-darken-2">mdi-chart-line</v-icon> 个人活动数据
          </v-card-title>
          <v-card-text>
            <!-- 幸运大转盘数据 -->
            <div class="activity-section">
              <div class="section-header section-header-with-action">
                <div class="d-flex align-center">
                  <v-icon size="small" color="purple-darken-1" class="mr-2">mdi-wheel-barrow</v-icon>
                  <span class="section-title">幸运大转盘</span>
                </div>
                <v-btn
                  size="small"
                  variant="text"
                  color="primary"
                  class="list-toggle-btn"
                  @click="wheelSectionExpanded = !wheelSectionExpanded"
                >
                  {{ wheelSectionExpanded ? '收起' : '展开' }}
                  <v-icon end size="small">{{ wheelSectionExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </div>
              <v-expand-transition>
                <div v-show="wheelSectionExpanded">
                  <div v-if="activityLoading" class="activity-loading">
                    <v-progress-circular 
                      indeterminate 
                      color="primary" 
                      size="30"
                      width="3"
                    ></v-progress-circular>
                    <span class="ml-2">加载中...</span>
                  </div>
                  
                  <div v-else class="activity-stats-grid">
                <!-- 今日数据 -->
                <div class="stats-card today-stats">
                  <div class="stats-card-header">
                    <v-icon size="small" color="orange-darken-2">mdi-weather-sunny</v-icon>
                    <span>今日数据</span>
                  </div>
                  <div class="stats-items">
                    <div class="stat-item">
                      <span class="stat-label">游戏次数</span>
                      <span class="stat-value today-value">{{ activityStats.today_spins }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">参与积分消耗</span>
                      <span class="stat-value today-value">-{{ activityStats.today_cost_credits.toFixed(1) }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">转盘积分变化</span>
                      <span 
                        class="stat-value today-value"
                        :class="activityStats.today_credits_change >= 0 ? 'positive' : 'negative'"
                      >
                        {{ activityStats.today_credits_change >= 0 ? '+' : '' }}{{ activityStats.today_credits_change.toFixed(1) }}
                      </span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">邀请码获得</span>
                      <span class="stat-value today-value">{{ activityStats.today_invite_codes }}</span>
                    </div>
                  </div>
                </div>

                <!-- 本周数据 -->
                <div class="stats-card week-stats">
                  <div class="stats-card-header">
                    <v-icon size="small" color="blue-darken-2">mdi-calendar-week</v-icon>
                    <span>本周数据</span>
                  </div>
                  <div class="stats-items">
                    <div class="stat-item">
                      <span class="stat-label">游戏次数</span>
                      <span class="stat-value week-value">{{ activityStats.week_spins }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">参与积分消耗</span>
                      <span class="stat-value week-value">-{{ activityStats.week_cost_credits.toFixed(1) }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">转盘积分变化</span>
                      <span 
                        class="stat-value week-value"
                        :class="activityStats.week_credits_change >= 0 ? 'positive' : 'negative'"
                      >
                        {{ activityStats.week_credits_change >= 0 ? '+' : '' }}{{ activityStats.week_credits_change.toFixed(1) }}
                      </span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">邀请码获得</span>
                      <span class="stat-value week-value">{{ activityStats.week_invite_codes }}</span>
                    </div>
                  </div>
                </div>

                <!-- 总计数据 -->
                <div class="stats-card total-stats">
                  <div class="stats-card-header">
                    <v-icon size="small" color="green-darken-2">mdi-chart-box</v-icon>
                    <span>历史总计</span>
                  </div>
                  <div class="stats-items">
                    <div class="stat-item">
                      <span class="stat-label">游戏次数</span>
                      <span class="stat-value total-value">{{ activityStats.total_spins }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">参与积分消耗</span>
                      <span class="stat-value total-value">-{{ activityStats.total_cost_credits.toFixed(1) }}</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">转盘积分变化</span>
                      <span 
                        class="stat-value total-value"
                        :class="activityStats.total_credits_change >= 0 ? 'positive' : 'negative'"
                      >
                        {{ activityStats.total_credits_change >= 0 ? '+' : '' }}{{ activityStats.total_credits_change.toFixed(1) }}
                      </span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-label">邀请码获得</span>
                      <span class="stat-value total-value">{{ activityStats.total_invite_codes }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 最近游戏记录 -->
              <div v-if="!activityLoading && activityStats.recent_games && activityStats.recent_games.length > 0" class="recent-games-section">
                <v-divider class="my-3"></v-divider>
                <div class="section-header section-header-with-action">
                  <div class="d-flex align-center">
                    <v-icon size="small" color="indigo-darken-1" class="mr-2">mdi-history</v-icon>
                    <span class="section-title">最近游戏记录</span>
                  </div>
                  <v-btn
                    size="small"
                    variant="text"
                    color="primary"
                    class="list-toggle-btn"
                    @click="wheelRecordsExpanded = !wheelRecordsExpanded"
                  >
                    {{ wheelRecordsExpanded ? '收起' : '展开' }}
                    <v-icon end size="small">{{ wheelRecordsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                </div>
                <v-expand-transition>
                  <div v-show="wheelRecordsExpanded" class="recent-games-list">
                    <div 
                      v-for="(game, index) in activityStats.recent_games" 
                      :key="index" 
                      class="recent-game-item"
                    >
                      <div class="game-result">
                        <v-chip 
                          size="small" 
                          :color="getGameResultColor(game.item_name)"
                          class="game-chip"
                          :title="game.item_name"
                        >
                          {{ game.item_name }}
                        </v-chip>
                      </div>
                      <div class="game-change">
                        <span 
                          class="change-value"
                          :class="game.credits_change >= 0 ? 'positive' : 'negative'"
                        >
                          {{ game.credits_change >= 0 ? '+' : '' }}{{ game.credits_change }}
                        </span>
                      </div>
                      <div class="game-date">
                        {{ formatGameDate(game.date) }}
                      </div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
                </div>
              </v-expand-transition>

              <!-- 夺宝奇兵数据 -->
              <div class="activity-section treasure-section">
                <v-divider class="my-3"></v-divider>
                <div class="section-header section-header-with-action">
                  <div class="d-flex align-center">
                    <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-treasure-chest</v-icon>
                    <span class="section-title">夺宝奇兵</span>
                  </div>
                  <v-btn
                    size="small"
                    variant="text"
                    color="primary"
                    class="list-toggle-btn"
                    @click="treasureSectionExpanded = !treasureSectionExpanded"
                  >
                    {{ treasureSectionExpanded ? '收起' : '展开' }}
                    <v-icon end size="small">{{ treasureSectionExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                </div>
                <v-expand-transition>
                  <div v-show="treasureSectionExpanded">
                    <div v-if="treasureLoading" class="activity-loading">
                      <v-progress-circular 
                        indeterminate 
                        color="amber-darken-2" 
                        size="30"
                        width="3"
                      ></v-progress-circular>
                      <span class="ml-2">加载中...</span>
                    </div>

                    <div v-else class="treasure-stats-grid">
                  <div class="stats-card treasure-stats">
                    <div class="stats-card-header">
                      <v-icon size="small" color="amber-darken-2">mdi-sword-cross</v-icon>
                      <span>夺宝统计</span>
                    </div>
                    <div class="stats-items">
                      <div class="stat-item">
                        <span class="stat-label">参与期数</span>
                        <span class="stat-value treasure-value">{{ treasureStats.participated_issues }}</span>
                      </div>
                      <div class="stat-item">
                        <span class="stat-label">消耗总积分</span>
                        <span class="stat-value treasure-cost-value">{{ treasureStats.total_cost_credits }}</span>
                      </div>
                      <div class="stat-item">
                        <span class="stat-label">中奖次数</span>
                        <span class="stat-value treasure-value">{{ treasureStats.win_count }}</span>
                      </div>
                      <div class="stat-item">
                        <span class="stat-label">中奖总积分</span>
                        <span class="stat-value treasure-win-value">{{ treasureStats.total_prize_credits }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!treasureLoading && treasureStats.recent_participations && treasureStats.recent_participations.length > 0"
                  class="recent-games-section"
                >
                  <v-divider class="my-3"></v-divider>
                  <div class="section-header section-header-with-action">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="brown-darken-1" class="mr-2">mdi-format-list-numbered</v-icon>
                      <span class="section-title">最近参与记录</span>
                    </div>
                    <v-btn
                      size="small"
                      variant="text"
                      color="primary"
                      class="list-toggle-btn"
                      @click="treasureRecordsExpanded = !treasureRecordsExpanded"
                    >
                      {{ treasureRecordsExpanded ? '收起' : '展开' }}
                      <v-icon end size="small">{{ treasureRecordsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </v-btn>
                  </div>
                  <v-expand-transition>
                    <div v-show="treasureRecordsExpanded" class="recent-games-list">
                      <div 
                        v-for="(record, index) in treasureStats.recent_participations" 
                        :key="`treasure-${index}`" 
                        class="recent-game-item"
                      >
                        <div class="game-result">
                          <v-chip
                            size="small"
                            :color="record.is_winner ? 'success' : 'blue-darken-1'"
                            class="game-chip"
                            :title="`第${record.issue_id}期`"
                          >
                            第{{ record.issue_id }}期
                          </v-chip>
                        </div>
                        <div class="game-change">
                          <span class="change-value negative">-{{ record.cost_credits }}</span>
                          <span v-if="record.won_credits > 0" class="change-value positive ml-1">+{{ record.won_credits }}</span>
                        </div>
                        <div class="game-date">
                          {{ formatTreasureDate(record.created_at) }}
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>
                  </div>
                </v-expand-transition>
              </div>

              <!-- 大预言家数据 -->
              <div class="activity-section prediction-section">
                <v-divider class="my-3"></v-divider>
                <div class="section-header section-header-with-action">
                  <div class="d-flex align-center">
                    <v-icon size="small" color="indigo-darken-1" class="mr-2">mdi-crystal-ball</v-icon>
                    <span class="section-title">大预言家</span>
                  </div>
                  <v-btn
                    size="small"
                    variant="text"
                    color="primary"
                    class="list-toggle-btn"
                    @click="predictionSectionExpanded = !predictionSectionExpanded"
                  >
                    {{ predictionSectionExpanded ? '收起' : '展开' }}
                    <v-icon end size="small">{{ predictionSectionExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                </div>
                <v-expand-transition>
                  <div v-show="predictionSectionExpanded">
                    <div class="treasure-stats-grid">
                      <div class="stats-card prediction-stats">
                        <div class="stats-card-header">
                          <v-icon size="small" color="indigo-darken-1">mdi-chart-box-outline</v-icon>
                          <span>预测统计</span>
                        </div>
                        <div class="stats-items">
                          <div class="stat-item">
                            <span class="stat-label">已结算题目</span>
                            <span class="stat-value prediction-value">{{ predictionStats.settled_markets || 0 }}</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">命中题目</span>
                            <span class="stat-value prediction-value">{{ predictionStats.win_markets || 0 }}</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">胜率</span>
                            <span class="stat-value prediction-value">{{ Number(predictionStats.win_rate || 0).toFixed(2) }}%</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">净盈亏</span>
                            <span
                              class="stat-value prediction-value"
                              :class="Number(predictionStats.net_profit || 0) >= 0 ? 'positive' : 'negative'"
                            >
                              {{ Number(predictionStats.net_profit || 0) >= 0 ? '+' : '' }}{{ Number(predictionStats.net_profit || 0).toFixed(2) }}
                            </span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">累计下注</span>
                            <span class="stat-value prediction-value">{{ Number(predictionStats.total_bet_amount || 0).toFixed(2) }}</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">累计返还</span>
                            <span class="stat-value prediction-value">{{ Number(predictionStats.total_payout_amount || 0).toFixed(2) }}</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">未结算题目</span>
                            <span class="stat-value prediction-value">{{ predictionStats.unsettled_markets || 0 }}</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">未结算占用</span>
                            <span class="stat-value prediction-value">{{ Number(predictionStats.unsettled_bet_amount || 0).toFixed(2) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="predictionStats.recent_settlements && predictionStats.recent_settlements.length > 0"
                      class="recent-games-section"
                    >
                      <v-divider class="my-3"></v-divider>
                      <div class="section-header section-header-with-action">
                        <div class="d-flex align-center">
                          <v-icon size="small" color="indigo-darken-1" class="mr-2">mdi-history</v-icon>
                          <span class="section-title">最近结算记录</span>
                        </div>
                        <v-btn
                          size="small"
                          variant="text"
                          color="primary"
                          class="list-toggle-btn"
                          @click="predictionRecordsExpanded = !predictionRecordsExpanded"
                        >
                          {{ predictionRecordsExpanded ? '收起' : '展开' }}
                          <v-icon end size="small">{{ predictionRecordsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </v-btn>
                      </div>
                      <v-expand-transition>
                        <div v-show="predictionRecordsExpanded" class="recent-games-list prediction-recent-list">
                          <div
                            v-for="(record, index) in predictionStats.recent_settlements"
                            :key="`prediction-${index}`"
                            class="recent-game-item"
                          >
                            <div class="game-result">
                              <v-chip
                                size="small"
                                :color="record.is_win ? 'success' : 'grey-darken-1'"
                                class="game-chip"
                                :title="record.title"
                              >
                                #{{ record.market_id }} {{ record.title }}
                              </v-chip>
                            </div>
                            <div class="game-change">
                              <span class="change-value negative">-{{ Number(record.bet_amount || 0).toFixed(2) }}</span>
                              <span v-if="Number(record.payout_amount || 0) > 0" class="change-value positive ml-1">+{{ Number(record.payout_amount || 0).toFixed(2) }}</span>
                              <span
                                class="change-value ml-1"
                                :class="Number(record.net_profit || 0) >= 0 ? 'positive' : 'negative'"
                              >
                                ({{ Number(record.net_profit || 0) >= 0 ? '+' : '' }}{{ Number(record.net_profit || 0).toFixed(2) }})
                              </span>
                            </div>
                            <div class="game-date">
                              {{ formatPredictionSettleDate(record.resolved_at) }}
                            </div>
                          </div>
                        </div>
                      </v-expand-transition>
                    </div>
                  </div>
                </v-expand-transition>
              </div>

              <!-- 21 点数据 -->
              <div class="activity-section blackjack-section">
                <v-divider class="my-3"></v-divider>
                <div class="section-header section-header-with-action">
                  <div class="d-flex align-center">
                    <v-icon size="small" color="pink-darken-1" class="mr-2">mdi-cards-playing</v-icon>
                    <span class="section-title">21 点</span>
                  </div>
                  <v-btn
                    size="small"
                    variant="text"
                    color="primary"
                    class="list-toggle-btn"
                    @click="blackjackSectionExpanded = !blackjackSectionExpanded"
                  >
                    {{ blackjackSectionExpanded ? '收起' : '展开' }}
                    <v-icon end size="small">{{ blackjackSectionExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </v-btn>
                </div>
                <v-expand-transition>
                  <div v-show="blackjackSectionExpanded">
                    <div v-if="blackjackLoading" class="activity-loading">
                      <v-progress-circular
                        indeterminate
                        color="primary"
                        size="30"
                        width="3"
                      ></v-progress-circular>
                      <span class="ml-2">加载中...</span>
                    </div>

                    <div v-else class="treasure-stats-grid">
                      <div class="stats-card blackjack-stats">
                        <div class="stats-card-header">
                          <v-icon size="small" color="pink-darken-1">mdi-cards-playing-outline</v-icon>
                          <span>牌局统计</span>
                        </div>
                        <div class="stats-items">
                          <div class="stat-item">
                            <span class="stat-label">累计手数</span>
                            <span class="stat-value blackjack-value">{{ blackjackStats.total_hands || 0 }}</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">胜率</span>
                            <span class="stat-value blackjack-value">{{ Number(blackjackStats.win_rate || 0).toFixed(2) }}%</span>
                          </div>
                          <div class="stat-item">
                            <!-- 投降手数与胜率并列：该投就投也是技巧，故单列展示而非藏进胜率里 -->
                            <span class="stat-label">投降手数</span>
                            <span class="stat-value blackjack-value">{{ blackjackStats.surrender_hands || 0 }}</span>
                          </div>
                          <div class="stat-item">
                            <!-- 决策准确率是 21 点里唯一纯粹反映技巧的口径，故置于醒目位置 -->
                            <span class="stat-label">决策准确率</span>
                            <span class="stat-value blackjack-accuracy-value">{{ Number(blackjackStats.accuracy || 0).toFixed(2) }}%</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">累计决策次数</span>
                            <span class="stat-value blackjack-value">{{ blackjackStats.decisions_total || 0 }}</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">净积分变动</span>
                            <span
                              class="stat-value blackjack-value"
                              :class="Number(blackjackStats.net_credits || 0) >= 0 ? 'positive' : 'negative'"
                            >
                              {{ Number(blackjackStats.net_credits || 0) >= 0 ? '+' : '' }}{{ Number(blackjackStats.net_credits || 0).toFixed(2) }}
                            </span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">单手最大赢利</span>
                            <span class="stat-value blackjack-win-value">{{ Number(blackjackStats.max_win || 0).toFixed(2) }}</span>
                          </div>
                          <div class="stat-item">
                            <span class="stat-label">累计奖池派彩</span>
                            <span class="stat-value blackjack-jackpot-value">{{ Number(blackjackStats.jackpot_total || 0).toFixed(2) }}</span>
                          </div>
                        </div>
                        <!-- 后端把奖池派彩与赔付分开记账，不注明的话这几个数字看着对不上 -->
                        <div class="stats-card-note">
                          净积分变动与单手最大赢利均不含奖池派彩
                        </div>
                      </div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- 交流群和通知频道链接 - 只有拥有账户的用户才能看到 -->
        <div v-if="(userInfo.plex_info || userInfo.emby_info) && (systemStatus.community_links?.group || systemStatus.community_links?.channel)" class="community-links-section">
          <v-card class="community-links-card">
            <v-card-title class="card-title-section">
              <v-icon start color="blue-darken-2">mdi-account-group</v-icon> 交流讨论
            </v-card-title>
            <v-card-text>
              <div class="community-links-grid">
                <!-- 交流群链接 -->
                <div 
                  v-if="systemStatus.community_links?.group"
                  class="community-link-item" 
                  @click="openCommunityLink('group')"
                  @contextmenu.prevent="showLinkContextMenu('group', $event)"
                  :title="'点击打开，右键复制链接'"
                >
                  <div class="link-icon-wrapper group-link">
                    <v-icon color="white" size="20">mdi-forum</v-icon>
                  </div>
                  <div class="link-content">
                    <div class="link-title">用户交流群</div>
                    <div class="link-description">随便聊聊</div>
                  </div>
                  <div class="link-actions">
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      @click.stop="copyLinkUrl('group')"
                      title="复制链接"
                      class="copy-link-btn"
                    >
                      <v-icon size="16">mdi-content-copy</v-icon>
                    </v-btn>
                    <v-icon color="primary" size="20">mdi-chevron-right</v-icon>
                  </div>
                </div>

                <!-- 通知频道链接 -->
                <div 
                  v-if="systemStatus.community_links?.channel && systemStatus.community_links.channel !== systemStatus.community_links.group"
                  class="community-link-item" 
                  @click="openCommunityLink('channel')"
                  @contextmenu.prevent="showLinkContextMenu('channel', $event)"
                  :title="'点击打开，右键复制链接'"
                >
                  <div class="link-icon-wrapper channel-link">
                    <v-icon color="white" size="20">mdi-bullhorn</v-icon>
                  </div>
                  <div class="link-content">
                    <div class="link-title">官方通知频道</div>
                    <div class="link-description">获取最新服务公告和更新</div>
                  </div>
                  <div class="link-actions">
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      @click.stop="copyLinkUrl('channel')"
                      title="复制链接"
                      class="copy-link-btn"
                    >
                      <v-icon size="16">mdi-content-copy</v-icon>
                    </v-btn>
                    <v-icon color="primary" size="20">mdi-chevron-right</v-icon>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <div v-if="!userInfo.plex_info && !userInfo.emby_info" class="no-accounts-message">
          <v-alert 
            type="info" 
            class="info-alert"
            rounded="lg"
            elevation="4"
          >
            <v-icon start>mdi-information</v-icon>
            您尚未绑定任何媒体服务账户，请先进行绑定
          </v-alert>
        </div>
      </div>
      </v-container>
    </div>
    
    <!-- 使用NSFW对话框组件 -->
    <nsfw-dialog 
      ref="nsfwDialog" 
      :current-credits="userInfo.credits"
      @operation-completed="handleNsfwOperationCompleted"
    />
    
    <!-- 使用捐赠对话框组件 -->
    <donation-dialog
      ref="donationDialog"
      @donation-submitted="handleDonationSubmitted"
    />
    
    <!-- 使用捐赠管理对话框组件 -->
    <donation-management-dialog
      ref="donationManagementDialog"
      @registration-submitted="handleDonationRegistrationSubmitted"
    />
    
    <!-- 使用积分商城对话框组件 -->
    <credits-store-dialog
      ref="creditsStoreDialog"
      @menu-selected="handleCreditsStoreMenuSelected"
    />
    
    <!-- 使用积分转移对话框组件 -->
    <credits-transfer-dialog
      ref="creditsTransferDialog"
      :current-credits="userInfo.credits"
      @transfer-completed="handleCreditsTransferCompleted"
    />
    
    <!-- 使用 Vaultwarden 兑换对话框组件 -->
    <vaultwarden-redeem-dialog
      ref="vaultwardenRedeemDialog"
      @redeem-success="handleVaultwardenRedeemSuccess"
    />
    
    <!-- 使用勋章中心对话框组件 -->
    <badge-center-dialog
      ref="badgeCenterDialog"
      :user-credits="userInfo.credits"
      @redeem-success="handleBadgeRedeemSuccess"
    />
    
    <!-- 使用Premium解锁对话框组件 -->
    <premium-unlock-dialog
      ref="premiumUnlockDialog"
      :current-credits="userInfo.credits"
      :current-premium-expiry="currentPremiumExpiry"
      :is-premium="currentIsPremium"
      @unlock-completed="handlePremiumUnlockCompleted"
    />
    
    <!-- 使用线路管理对话框组件 -->
    <line-management-dialog
      ref="lineManagementDialog"
      @lines-updated="handleLinesUpdated"
      @tags-updated="handleTagsUpdated"
    />
    
    <!-- Plex 线路调度对话框 -->
    <line-schedule-dialog
      v-model:show="showPlexScheduleDialog"
      initial-service="plex"
      @success="handleScheduleSuccess"
      @error="handleScheduleError"
    />
    
    <!-- Emby 线路调度对话框 -->
    <line-schedule-dialog
      v-model:show="showEmbyScheduleDialog"
      initial-service="emby"
      @success="handleScheduleSuccess"
      @error="handleScheduleError"
    />
    
    <!-- 下载权限解锁对话框 -->
    <download-unlock-dialog
      ref="downloadUnlockDialog"
      :current-credits="userInfo.credits"
      @unlocked="handleDownloadUnlocked"
    />
  </div>
</template>

<script>
import { getUserInfo } from '@/api'
import { getSystemStatus } from '@/services/systemService'
import EmbyLineSelector from '@/components/EmbyLineSelector.vue'
import PlexLineSelector from '@/components/PlexLineSelector.vue'
import NsfwDialog from '@/components/NsfwDialog.vue'
import DonationDialog from '@/components/DonationDialog.vue'
import DonationManagementDialog from '@/components/DonationManagementDialog.vue'
import CreditsTransferDialog from '@/components/CreditsTransferDialog.vue'
import CreditsStoreDialog from '@/components/CreditsStoreDialog.vue'
import VaultwardenRedeemDialog from '@/components/VaultwardenRedeemDialog.vue'
import BadgeCenterDialog from '@/components/BadgeCenterDialog.vue'
import PremiumUnlockDialog from '@/components/PremiumUnlockDialog.vue'
import DownloadUnlockDialog from '@/components/DownloadUnlockDialog.vue'
import LineManagementDialog from '@/components/LineManagementDialog.vue'
import LineScheduleDialog from '@/components/LineScheduleDialog.vue'
import { getWatchLevelIcons, showNoWatchTimeText } from '@/utils/watchLevel.js'
import { redeemInviteCodeForCredits } from '@/services/inviteCodeService.js'
import { checkPrivilegedInviteCode, batchCheckPrivilegedInviteCodes } from '@/services/mediaServiceApi.js'
import { getUserActivityStats } from '@/services/wheelService.js'
import { getUserTreasureStats } from '@/services/treasureService.js'
import { getUserPredictionStats } from '@/services/predictionService.js'
import { getUserBlackjackStats } from '@/services/blackjackService.js'
import { formatTraffic } from '@/utils/format.js'
import { getMyBadges } from '@/services/badgeService.js'

export default {
  name: 'UserInfo',
  components: {
    EmbyLineSelector,
    PlexLineSelector,
    NsfwDialog,
    DonationDialog,
    DonationManagementDialog,
    CreditsTransferDialog,
    CreditsStoreDialog,
    VaultwardenRedeemDialog,
    BadgeCenterDialog,
    PremiumUnlockDialog,
    LineManagementDialog,
    LineScheduleDialog,
    DownloadUnlockDialog
  },
  data() {
    return {
      userInfo: {
        credits: 0,
        donation: 0,
        invitee_count: 0,
        invitation_codes: [],
        plex_info: {
          line: null,
          daily_traffic: 0,
          daily_premium_traffic: 0,
          daily_premium_free_remaining: 0,
          daily_premium_free_limit: 0,
          daily_premium_debt: 0,
          daily_premium_projected_debt: 0
        },
        emby_info: {
          line: null,
          daily_traffic: 0,
          daily_premium_traffic: 0,
          daily_premium_free_remaining: 0,
          daily_premium_free_limit: 0,
          daily_premium_debt: 0,
          daily_premium_projected_debt: 0
        },
        overseerr_info: null,
        is_admin: false
      },
      loading: true,
      error: null,
      redeemingCodes: {}, // 用于跟踪每个邀请码的兑换状态
      privilegedCodes: {}, // 用于跟踪特权邀请码状态
      activityStats: {
        today_spins: 0,
        total_spins: 0,
        week_spins: 0,
        total_credits_change: 0.0,
        today_credits_change: 0.0,
        week_credits_change: 0.0,
        total_cost_credits: 0.0,
        today_cost_credits: 0.0,
        week_cost_credits: 0.0,
        total_invite_codes: 0,
        today_invite_codes: 0,
        week_invite_codes: 0,
        recent_games: []
      },
      activityLoading: false,
      wheelSectionExpanded: false,
      wheelRecordsExpanded: false,
      treasureStats: {
        participated_issues: 0,
        total_cost_credits: 0,
        win_count: 0,
        total_prize_credits: 0,
        recent_participations: []
      },
      treasureLoading: false,
      treasureSectionExpanded: false,
      treasureRecordsExpanded: false,
      predictionSectionExpanded: false,
      predictionRecordsExpanded: false,
      predictionStats: {
        settled_markets: 0,
        win_markets: 0,
        win_rate: 0,
        total_bet_amount: 0,
        total_payout_amount: 0,
        net_profit: 0,
        unsettled_markets: 0,
        unsettled_bet_amount: 0,
        recent_settlements: []
      },
      blackjackSectionExpanded: false,
      blackjackLoading: false,
      // 净积分与单手最大赢利均**不含**奖池派彩，后端如此分开记账：
      // 奖池衡量的是运气，混进去会让这两项失去反映打法的意义
      blackjackStats: {
        total_hands: 0,
        net_credits: 0,
        max_win: 0,
        win_rate: 0,
        accuracy: 0,
        decisions_total: 0,
        jackpot_total: 0,
        // 投降手数：计入胜率分母、不计入分子（与平局同一待遇）
        surrender_hands: 0
      },
      systemStatus: {
        site_name: '', // 默认值，从后端获取后会更新
        emby_entry_url: '', // 默认值，从后端获取后会更新
        premium_unlock_enabled: true,
        premium_free: false,
        community_links: {
          group: '',
          channel: ''
        }
      },
      creditsTransferEnabled: true, // 积分转移功能开关状态
      currentPremiumExpiry: null,
      currentIsPremium: false,
      showPlexScheduleDialog: false,
      showEmbyScheduleDialog: false,
      userBadges: [], // 用户拥有的勋章列表
      badgesLoading: false
    }
  },
  mounted() {
    this.fetchUserInfo()
    this.fetchActivityStats()
    this.fetchTreasureStats()
    this.fetchPredictionStats()
    this.fetchBlackjackStats()
    this.fetchSystemStatus() // 这里会同时获取系统状态和积分转移开关状态
    this.fetchUserBadges()
  },
  methods: {
    // 格式化流量显示
    formatTraffic(bytes) {
      return formatTraffic(bytes)
    },

    // 格式化勋章过期时间
    formatBadgeExpireDate(timestamp) {
      try {
        // 检查时间戳是否有效
        if (!timestamp || timestamp <= 0) {
          return '未知'
        }
        
        // 后端返回的是秒级时间戳，转换为毫秒
        const milliseconds = timestamp * 1000
        
        // 检查是否超过合理范围（当前时间 + 100 年）
        const maxReasonableDate = new Date().getTime() + (100 * 365 * 24 * 60 * 60 * 1000)
        if (milliseconds > maxReasonableDate) {
          return '永久'
        }
        
        const date = new Date(milliseconds)
        
        // 检查日期是否有效
        if (isNaN(date.getTime())) {
          return '永久'
        }
        
        return date.toLocaleDateString()
      } catch (error) {
        console.error('格式化勋章过期时间失败:', error, 'timestamp:', timestamp)
        return '永久'
      }
    },

    async fetchUserInfo() {
      try {
        this.loading = true
        const response = await getUserInfo()
        this.userInfo = response.data
        
        // 检查每个邀请码的特权状态
        if (this.userInfo.invitation_codes && this.userInfo.invitation_codes.length > 0) {
          await this.checkPrivilegedCodes()
        }
        
        this.loading = false
      } catch (err) {
        this.error = err.response?.data?.detail || '获取用户信息失败'
        this.loading = false
        console.error('获取用户信息失败:', err)
      }
    },

    // 获取活动数据
    async fetchActivityStats() {
      try {
        this.activityLoading = true
        const response = await getUserActivityStats()
        if (response.data && response.data.success) {
          this.activityStats = {
            ...this.activityStats,
            ...response.data.data
          }
        }
      } catch (err) {
        console.error('获取活动统计数据失败:', err)
        // 不显示错误，使用默认值
      } finally {
        this.activityLoading = false
      }
    },

    // 获取夺宝统计数据
    async fetchTreasureStats() {
      try {
        this.treasureLoading = true
        const response = await getUserTreasureStats()
        if (response.data && response.data.success) {
          this.treasureStats = response.data.data
        }
      } catch (err) {
        console.error('获取夺宝统计数据失败:', err)
        // 不显示错误，使用默认值
      } finally {
        this.treasureLoading = false
      }
    },

    // 获取大预言家统计数据
    async fetchPredictionStats() {
      try {
        const response = await getUserPredictionStats()
        if (response.data && response.data.success) {
          this.predictionStats = {
            ...this.predictionStats,
            ...response.data.data
          }
        }
      } catch (err) {
        console.error('获取大预言家统计数据失败:', err)
        // 不显示错误，使用默认值
      }
    },

    // 获取 21 点统计数据。
    // 该接口直接返回统计对象，没有 {success, data} 外壳，与夺宝/大预言家不同
    async fetchBlackjackStats() {
      try {
        this.blackjackLoading = true
        const response = await getUserBlackjackStats()
        if (response.data) {
          this.blackjackStats = {
            ...this.blackjackStats,
            ...response.data
          }
        }
      } catch (err) {
        console.error('获取 21 点统计数据失败:', err)
        // 不显示错误，使用默认值
      } finally {
        this.blackjackLoading = false
      }
    },

    // 获取系统状态
    async fetchSystemStatus() {
      try {
        const response = await getSystemStatus()
        this.systemStatus = response
        // 同时获取积分转移开关状态
        this.creditsTransferEnabled = response.credits_transfer_enabled !== undefined ? 
          response.credits_transfer_enabled : true
      } catch (err) {
        console.error('获取系统状态失败:', err)
        // 使用默认值，不影响用户体验
        this.creditsTransferEnabled = true
      }
    },

    // 获取用户勋章
    async fetchUserBadges() {
      try {
        this.badgesLoading = true
        const response = await getMyBadges()
        this.userBadges = response.data || []
      } catch (err) {
        console.error('获取用户勋章失败:', err)
        // 不显示错误，使用默认值
        this.userBadges = []
      } finally {
        this.badgesLoading = false
      }
    },

    // 检查特权邀请码
    async checkPrivilegedCodes() {
      // 重置特权码状态映射
      this.privilegedCodes = {};
      
      if (!this.userInfo.invitation_codes || this.userInfo.invitation_codes.length === 0) {
        return;
      }
      
      try {
        // 使用批量检查API，一次性检查所有邀请码
        const result = await batchCheckPrivilegedInviteCodes(this.userInfo.invitation_codes);
        
        // 将结果映射到索引
        this.userInfo.invitation_codes.forEach((code, index) => {
          this.privilegedCodes[index] = result.results[code] || false;
        });
        
        console.log(`批量检查 ${this.userInfo.invitation_codes.length} 个邀请码完成`);
      } catch (error) {
        console.error('批量检查邀请码特权状态失败:', error);
        // 出错时使用回退方案：逐个检查
        console.log('使用回退方案：逐个检查邀请码');
        await this.checkPrivilegedCodesLegacy();
      }
    },

    // 回退方案：逐个检查邀请码（保留原有逻辑作为备用）
    async checkPrivilegedCodesLegacy() {
      this.privilegedCodes = {};
      
      for (let i = 0; i < this.userInfo.invitation_codes.length; i++) {
        const code = this.userInfo.invitation_codes[i]
        try {
          const result = await checkPrivilegedInviteCode(code)
          this.privilegedCodes[i] = result.privileged
        } catch (error) {
          console.error(`检查邀请码 ${code} 特权状态失败:`, error)
          this.privilegedCodes[i] = false
        }
      }
    },
    
    showMessage(message, type = 'success') {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup({
          title: type === 'error' ? '错误' : '成功',
          message: message
        })
      } else {
        alert(message)
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showPopup({
            title: '复制成功',
            message: '已复制到剪贴板'
          })
        } else {
          alert('已复制到剪贴板')
        }
      }).catch(err => {
        console.error('复制失败:', err)
      })
    },

    // 兑换邀请码为积分
    async redeemCodeForCredits(code, index) {
      // 显示确认提示框
      const confirmed = await this.showConfirmDialog(
        '确认兑换',
        `确定要兑换邀请码 "${code}" 为积分吗？\n\n兑换后将获得该邀请码价值 80% 的积分，且邀请码将被消耗。`
      );

      if (!confirmed) {
        return; // 用户取消了操作
      }

      try {
        // 设置加载状态
        this.redeemingCodes[index] = true;
        
        const response = await redeemInviteCodeForCredits(code);
        
        if (response.success) {
          // 更新用户积分
          this.userInfo.credits = response.current_credits;
          
          // 从邀请码列表中移除已兑换的邀请码
          this.userInfo.invitation_codes.splice(index, 1);
          
          // 更新特权码状态映射 - 移除对应索引，并重新映射后续索引
          const newPrivilegedCodes = {};
          Object.keys(this.privilegedCodes).forEach(key => {
            const keyIndex = parseInt(key);
            if (keyIndex < index) {
              // 保持原索引
              newPrivilegedCodes[keyIndex] = this.privilegedCodes[keyIndex];
            } else if (keyIndex > index) {
              // 索引向前移动
              newPrivilegedCodes[keyIndex - 1] = this.privilegedCodes[keyIndex];
            }
            // 跳过被删除的索引
          });
          this.privilegedCodes = newPrivilegedCodes;
          
          // 同样更新兑换状态映射
          const newRedeemingCodes = {};
          Object.keys(this.redeemingCodes).forEach(key => {
            const keyIndex = parseInt(key);
            if (keyIndex < index) {
              newRedeemingCodes[keyIndex] = this.redeemingCodes[keyIndex];
            } else if (keyIndex > index) {
              newRedeemingCodes[keyIndex - 1] = this.redeemingCodes[keyIndex];
            }
          });
          this.redeemingCodes = newRedeemingCodes;
          
          // 显示成功消息
          const message = `成功兑换！获得 ${response.credits_earned.toFixed(2)} 积分`;
          this.showMessage(message, 'success');
          
          if (window.Telegram?.WebApp) {
            window.Telegram.WebApp.showPopup({
              title: '兑换成功',
              message: message
            });
          }
        } else {
          // 显示错误消息
          this.showMessage(response.message, 'error');
        }
      } catch (error) {
        console.error('兑换邀请码失败:', error);
        this.showMessage('兑换失败，请稍后重试', 'error');
      } finally {
        // 清除加载状态
        if (this.redeemingCodes[index] !== undefined) {
          this.redeemingCodes[index] = false;
        }
      }
    },

    // 显示确认对话框
    showConfirmDialog(title, message) {
      return new Promise((resolve) => {
        if (window.Telegram?.WebApp) {
          // 在 Telegram 环境中使用原生确认对话框
          window.Telegram.WebApp.showConfirm(message, (confirmed) => {
            resolve(confirmed);
          });
        } else {
          // 在开发环境中使用浏览器的 confirm
          const confirmed = confirm(`${title}\n\n${message}`);
          resolve(confirmed);
        }
      });
    },

    updateEmbyLine(line) {
      if (this.userInfo && this.userInfo.emby_info) {
        this.userInfo.emby_info.line = line;
      }
    },
    
    updatePlexLine(line) {
      if (this.userInfo && this.userInfo.plex_info) {
        this.userInfo.plex_info.line = line;
      }
    },
    
    // 打开 Plex 线路调度对话框
    openPlexScheduleDialog() {
      this.showPlexScheduleDialog = true;
    },
    
    // 打开 Emby 线路调度对话框
    openEmbyScheduleDialog() {
      this.showEmbyScheduleDialog = true;
    },
    
    // 处理调度成功
    handleScheduleSuccess(message) {
      this.$emit('show-snackbar', { message, color: 'success' });
    },
    
    // 处理调度错误
    handleScheduleError(message) {
      this.$emit('show-snackbar', { message, color: 'error' });
    },
    
    // 打开NSFW权限管理对话框
    openNsfwDialog(service, isAllLib) {
      // 通过引用调用子组件方法
      this.$refs.nsfwDialog.open(service, isAllLib);
    },
    
    // 处理NSFW操作完成后的状态更新
    handleNsfwOperationCompleted(result) {
      const { service, isUnlock, cost } = result;
      
      // 更新资料库权限状态
      if (service === 'plex' && this.userInfo.plex_info) {
        this.userInfo.plex_info.all_lib = isUnlock;
      } else if (service === 'emby' && this.userInfo.emby_info) {
        this.userInfo.emby_info.all_lib = isUnlock;
      }
      
      // 更新用户积分
      this.userInfo.credits -= cost;
    },
    // 使用导入的方法获取观看等级图标
    watchLevelIcons(watchedTime) {
      return getWatchLevelIcons(watchedTime);
    },
    
    // 检查是否显示"暂无观看记录"文本
    showNoWatchTimeText(watchedTime) {
      return showNoWatchTimeText(watchedTime);
    },

    // 显示观看时长对话框
    showWatchTimeDialog(watchedTime) {
      const message = `观看时长：${watchedTime.toFixed(2)} 小时`;
      
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup({
          title: '观看时长信息',
          message: message
        });
      } else {
        alert(message);
      }
    },
    
    // 打开捐赠对话框
    openDonationDialog() {
      this.$refs.donationDialog.open();
    },
    
    // 打开捐赠管理对话框
    openDonationManagementDialog() {
      this.$refs.donationManagementDialog.open();
    },
    
    // 打开积分商城对话框
    openCreditsStoreDialog() {
      this.$refs.creditsStoreDialog.open();
    },
    
    // 处理积分商城菜单选择
    handleCreditsStoreMenuSelected(menuValue) {
      switch (menuValue) {
        case 'transfer':
          // 打开积分转移对话框
          this.openCreditsTransferDialog();
          break;
        case 'vaultwarden':
          // 打开 Vaultwarden 兑换对话框
          this.openVaultwardenRedeemDialog();
          break;
        case 'badges':
          // 打开勋章中心对话框
          this.openBadgeCenterDialog();
          break;
        // 可以在这里添加其他菜单项的处理
        // case 'exchange':
        //   this.openCreditsExchangeDialog();
        //   break;
        // case 'history':
        //   this.openCreditsHistoryDialog();
        //   break;
        default:
          break;
      }
    },
    
    // 处理积分转移按钮点击事件
    handleCreditsTransferClick() {
      if (!this.creditsTransferEnabled) {
        // 功能关闭时显示提示
        this.showMessage('积分转移功能暂时关闭', 'error')
        return
      }
      
      // 功能开启时正常打开对话框
      this.openCreditsTransferDialog()
    },
    
    // 打开积分转移对话框
    openCreditsTransferDialog() {
      this.$refs.creditsTransferDialog.open();
    },
    
    // 处理积分转移完成事件
    handleCreditsTransferCompleted(result) {
      const { amount, target_user, current_credits } = result;
      
      // 更新用户积分
      this.userInfo.credits = current_credits;
      
      // 显示成功消息
      this.showMessage(`成功转移 ${amount} 积分给用户 ${target_user}`, 'success');
    },
    
    // 打开 Vaultwarden 兑换对话框
    openVaultwardenRedeemDialog() {
      this.$refs.vaultwardenRedeemDialog.open();
    },
    
    // 处理 Vaultwarden 兑换成功事件
    handleVaultwardenRedeemSuccess(result) {
      const { credits_deducted, remaining_credits } = result;
      
      // 更新用户积分
      this.userInfo.credits = remaining_credits;
      
      // 显示成功消息
      this.showMessage(`成功兑换 Vaultwarden 账户，扣除 ${credits_deducted} 积分`, 'success');
    },
    
    // 打开勋章中心对话框
    openBadgeCenterDialog() {
      this.$refs.badgeCenterDialog.open();
    },
    
    // 处理勋章兑换成功事件
    handleBadgeRedeemSuccess(result) {
      const { badge_name, credits_cost, remaining_credits } = result;
      
      // 更新用户积分
      this.userInfo.credits = remaining_credits;
      
      // 重新加载勋章数据
      this.fetchUserBadges();
      
      // 显示成功消息
      this.showMessage(`成功兑换勋章「${badge_name}」，消耗 ${credits_cost} 积分`, 'success');
    },
    
    // 处理捐赠提交事件
    handleDonationSubmitted() {
      // 重新获取用户信息以更新捐赠金额
      this.fetchUserInfo();
    },
    
    // 处理捐赠登记提交事件
    handleDonationRegistrationSubmitted() {
      // 显示成功消息
      this.showMessage('捐赠登记提交成功！管理员将在24小时内处理。', 'success');
      // 可以选择是否重新获取用户信息，因为登记尚未被确认
      // this.fetchUserInfo();
    },
    
    // 打开线路管理对话框
    openLineManagementDialog() {
      this.$refs.lineManagementDialog.open();
    },
    
    // 打开Premium解锁对话框
    openPremiumUnlockDialog(serviceType) {
      const serviceInfo = serviceType === 'plex' ? 
        this.userInfo.plex_info : 
        this.userInfo.emby_info;
      
      this.currentPremiumExpiry = serviceInfo?.premium_expiry || null;
      this.currentIsPremium = serviceInfo?.is_premium || false;
      
      // 直接传入服务信息，避免使用 props 导致的跨服务状态混淆
      this.$refs.premiumUnlockDialog.open(serviceType, serviceInfo);
    },
    
    // 打开下载权限解锁对话框
    openDownloadUnlockDialog(serviceType) {
      this.$refs.downloadUnlockDialog.open(serviceType);
    },
    
    // 处理下载权限解锁完成事件
    handleDownloadUnlocked(result) {
      const { service, remaining_credits } = result;
      
      // 更新用户积分
      this.userInfo.credits = remaining_credits;
      
      // 更新下载权限状态
      if (service === 'plex' && this.userInfo.plex_info) {
        this.userInfo.plex_info.download_unlocked = true;
        this.userInfo.plex_info.download_unlock_time = Date.now();
      } else if (service === 'emby' && this.userInfo.emby_info) {
        this.userInfo.emby_info.download_unlocked = true;
        this.userInfo.emby_info.download_unlock_time = Date.now();
      }
      
      this.showMessage(`成功解锁 ${service.toUpperCase()} 下载权限`);
    },

    // 处理Premium解锁完成事件
    handlePremiumUnlockCompleted(result) {
      const { service, days, cost, current_credits, premium_expiry } = result;
      
      // 更新用户积分
      this.userInfo.credits = current_credits;
      
      // 更新Premium状态和到期时间
      if (service === 'plex' && this.userInfo.plex_info) {
        this.userInfo.plex_info.is_premium = true;
        this.userInfo.plex_info.premium_expiry = premium_expiry;
      } else if (service === 'emby' && this.userInfo.emby_info) {
        this.userInfo.emby_info.is_premium = true;
        this.userInfo.emby_info.premium_expiry = premium_expiry;
      }

      this.showMessage(`成功解锁 ${days} 天 Premium 会员，消耗 ${cost} 积分`);
    },

    // 格式化Premium到期时间
    formatPremiumExpiry(expiryTime) {
      if (!expiryTime) return '永久';
      try {
        return new Date(expiryTime).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return expiryTime;
      }
    },

    // 检查Premium是否即将到期
    isPremiumExpiringSoon(expiryTime) {
      if (!expiryTime) return false;
      try {
        const expiry = new Date(expiryTime);
        const now = new Date();
        const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        return diffDays <= 3 && diffDays > 0;
      } catch (error) {
        return false;
      }
    },

    // 格式化最后观看时间
    formatLastViewedAt(timestamp) {
      if (!timestamp || timestamp === 0) return '暂无观看记录';
      try {
        const date = new Date(timestamp * 1000); // 时间戳转换为毫秒
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        // 如果是今天
        if (diffDays === 0) {
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          if (diffHours === 0) {
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            if (diffMinutes === 0) {
              return '刚刚';
            }
            return `${diffMinutes} 分钟前`;
          }
          return `${diffHours} 小时前`;
        }
        
        // 如果是昨天
        if (diffDays === 1) {
          return '昨天 ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
        }
        
        // 如果在7天内
        if (diffDays < 7) {
          return `${diffDays} 天前`;
        }
        
        // 其他情况显示完整日期
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return '无效时间';
      }
    },

    // 根据游戏结果获取颜色
    getGameResultColor(itemName) {
      if (itemName.includes('邀请码')) {
        return 'amber-darken-2'
      } else if (itemName.includes('+')) {
        return 'success'
      } else if (itemName.includes('-')) {
        return 'error'
      } else if (itemName.includes('翻倍')) {
        return 'purple'
      } else if (itemName.includes('减半')) {
        return 'orange-darken-2'
      } else {
        return 'grey'
      }
    },

    // 格式化游戏日期
    formatGameDate(dateString) {
      try {
        const today = new Date().toDateString()
        const gameDate = new Date(dateString).toDateString()
        
        if (today === gameDate) {
          return '今天'
        }
        
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        if (yesterday.toDateString() === gameDate) {
          return '昨天'
        }
        
        return dateString
      } catch (error) {
        return dateString
      }
    },

    // 格式化夺宝记录时间
    formatTreasureDate(dateValue) {
      if (!dateValue) return '未知时间'
      try {
        const date = new Date(dateValue)
        if (Number.isNaN(date.getTime())) {
          return '未知时间'
        }

        const now = new Date()
        const diffMs = now - date
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffDays === 0) {
          return '今天'
        }
        if (diffDays === 1) {
          return '昨天'
        }
        return date.toLocaleDateString('zh-CN')
      } catch (error) {
        return '未知时间'
      }
    },

    // 格式化大预言家结算时间
    formatPredictionSettleDate(timestamp) {
      if (!timestamp) return '未知时间'
      try {
        const date = new Date(Number(timestamp) * 1000)
        if (Number.isNaN(date.getTime())) {
          return '未知时间'
        }

        const now = new Date()
        const diffMs = now - date
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffDays === 0) {
          return '今天'
        }
        if (diffDays === 1) {
          return '昨天'
        }
        return date.toLocaleDateString('zh-CN')
      } catch (error) {
        return '未知时间'
      }
    },

    // 打开社区链接
    openCommunityLink(type) {
      let url = '';
      let title = '';
      
      if (type === 'group') {
        url = this.systemStatus.community_links?.group || '';
        title = '用户交流群';
      } else if (type === 'channel') {
        url = this.systemStatus.community_links?.channel || '';
        title = '官方通知频道';
      }

      if (url) {
        if (window.Telegram?.WebApp) {
          // 在 Telegram 环境中打开链接
          window.Telegram.WebApp.openTelegramLink(url);
          this.showMessage(`正在打开${title}...`, 'success');
        } else {
          // 在浏览器环境中的处理
          try {
            // 尝试直接打开链接
            const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
            
            // 检查是否被弹窗拦截器拦截
            if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
              // 被拦截，显示手动打开提示
              this.showBrowserLinkDialog(url, title);
            } else {
              // 成功打开
              this.showMessage(`正在打开${title}...`, 'success');
            }
          } catch (error) {
            console.error('打开链接失败:', error);
            // 出错时也显示手动打开提示
            this.showBrowserLinkDialog(url, title);
          }
        }
      } else {
        this.showMessage(`${title}链接暂未配置`, 'error');
      }
    },

    // 在浏览器中显示链接对话框（用于被弹窗拦截的情况）
    showBrowserLinkDialog(url, title) {
      // 创建一个简单的确认对话框
      const message = `浏览器阻止了弹窗，请手动打开${title}：\n\n${url}\n\n点击确定复制链接到剪贴板`;
      
      if (confirm(message)) {
        // 用户确认后复制链接到剪贴板
        this.copyToClipboard(url);
        this.showMessage('链接已复制到剪贴板，请手动打开', 'success');
      }
    },

    // 复制社区链接URL
    copyLinkUrl(type) {
      let url = '';
      let title = '';
      
      if (type === 'group') {
        url = this.systemStatus.community_links?.group || '';
        title = '用户交流群';
      } else if (type === 'channel') {
        url = this.systemStatus.community_links?.channel || '';
        title = '官方通知频道';
      }

      if (url) {
        this.copyToClipboard(url);
        this.showMessage(`${title}链接已复制`, 'success');
      } else {
        this.showMessage(`${title}链接暂未配置`, 'error');
      }
    },

    // 显示链接右键菜单
    showLinkContextMenu(type, event) {
      // 阻止默认右键菜单
      event.preventDefault();
      
      let title = type === 'group' ? '用户交流群' : '官方通知频道';
      
      // 在非Telegram环境中提供复制选项
      if (!window.Telegram?.WebApp) {
        const message = `${title}选项：\n1. 打开链接\n2. 复制链接\n\n请选择操作（确定=打开链接，取消=复制链接）`;
        
        if (confirm(message)) {
          this.openCommunityLink(type);
        } else {
          this.copyLinkUrl(type);
        }
      }
    },
  }
}
</script>

<style scoped>
.user-info-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  padding-bottom: 80px; /* 为底部导航栏留出空间 */
}

.content-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

.user-info-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.transparent-container {
  background: transparent !important;
  padding: 0 !important;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  padding: 40px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 30px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.loading-text {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 错误状态样式 */
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 20px;
}

.error-alert {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
  border: none !important;
  max-width: 500px;
}

/* 勋章样式 */
.badge-icon {
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid #FFA000;
}

.badge-icon:hover {
  transform: translateY(-2px) scale(1.1);
  box-shadow: 0 4px 12px rgba(255, 160, 0, 0.4) !important;
}

.badge-expired {
  opacity: 0.5;
  border-color: #9E9E9E !important;
}

.badge-tooltip {
  text-align: center;
  padding: 4px;
}

.gap-2 {
  gap: 8px;
}

.user-info-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: none;
}

.user-info-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.card-title-section {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px 16px 0 0;
  border-bottom: 1px solid rgba(102, 126, 234, 0.2);
  padding: 20px 24px 16px;
}

/* 确保卡片内容左对齐 */
.user-info-card .v-card-text {
  text-align: left;
  padding: 24px;
}

/* 无账户消息样式 */
.no-accounts-message {
  text-align: center;
  margin: 40px 0;
}

.info-alert {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
  border: none !important;
}

/* 通用芯片样式 */
.v-chip {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  font-weight: 500;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.v-chip.v-chip--size-small {
  height: 28px !important;
  font-size: 12px !important;
  padding: 0 12px !important;
}

/* Vuetify 主题颜色覆盖 */
.v-chip.v-chip--variant-flat.text-success {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%) !important;
  color: #2E7D32 !important;
  border: 1px solid rgba(76, 175, 80, 0.3) !important;
}

.v-chip.v-chip--variant-flat.text-warning {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 152, 0, 0.08) 100%) !important;
  color: #E65100 !important;
  border: 1px solid rgba(255, 152, 0, 0.3) !important;
}

.v-chip.v-chip--variant-flat.text-primary {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%) !important;
  color: #3F51B5 !important;
  border: 1px solid rgba(102, 126, 234, 0.3) !important;
}

.v-chip.v-chip--variant-flat.text-amber-darken-2 {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 193, 7, 0.08) 100%) !important;
  color: #F57C00 !important;
  border: 1px solid rgba(255, 193, 7, 0.3) !important;
}

/* 特权邀请码芯片样式 - 移除蓝色边框 */
.invitation-chip-horizontal.text-amber-darken-2 {
  border: 1px solid rgba(255, 193, 7, 0.3) !important;
  box-shadow: none !important;
}

.invitation-chip-horizontal.text-amber-darken-2:hover {
  border: 1px solid rgba(255, 193, 7, 0.5) !important;
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2) !important;
}

/* 可点击的芯片样式 */
.clickable-chip {
  cursor: pointer;
  transition: all 0.3s ease;
}

.clickable-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* 邀请码芯片样式 */
.invitation-chip {
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%) !important;
  border: 1px solid rgba(102, 126, 234, 0.3) !important;
  backdrop-filter: blur(8px);
}

/* 新增：水平布局的邀请码芯片样式 */
.invitation-chip-horizontal {
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  max-width: 100%;
  min-width: 0;
}

/* 普通邀请码的样式 */
.invitation-chip-horizontal:not(.text-amber-darken-2) {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%) !important;
  border: 1px solid rgba(102, 126, 234, 0.3) !important;
  backdrop-filter: blur(8px);
}

.invitation-chip:hover,
.invitation-chip-horizontal:not(.text-amber-darken-2):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* 邀请码文本样式 */
.code-text {
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
  letter-spacing: 0.5px;
}

/* 值显示样式 */
.value-display {
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  min-width: 60px;
  text-align: center;
  backdrop-filter: blur(5px);
}

.credits-value {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%);
  color: #F57C00;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.donation-value {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%);
  color: #388E3C;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.invitee-value {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(156, 39, 176, 0.05) 100%);
  color: #7B1FA2;
  border: 1px solid rgba(156, 39, 176, 0.2);
}

.traffic-value {
  background: linear-gradient(135deg, rgba(96, 125, 139, 0.1) 0%, rgba(96, 125, 139, 0.05) 100%);
  color: #455A64;
  border: 1px solid rgba(96, 125, 139, 0.2);
}

.premium-traffic {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 193, 7, 0.08) 100%);
  color: #F57C00;
  border: 1px solid rgba(255, 193, 7, 0.3);
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.1);
}

.free-premium-remaining {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%);
  color: #2E7D32;
  border: 1px solid rgba(76, 175, 80, 0.25);
}

.quota-tooltip {
  font-size: 12px;
  line-height: 1.6;
  white-space: nowrap;
}

.quota-trigger {
  cursor: pointer;
}

/* 观看等级图标样式 */
.level-icons-container {
  display: flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 2px 6px;
  border-radius: 8px;
}

.level-icons-container:hover {
  background: rgba(102, 126, 234, 0.1);
  transform: scale(1.05);
}

.emoji-icon {
  font-size: 16px;
  transition: transform 0.2s ease;
}

.emoji-icon:hover {
  transform: scale(1.1);
}

/* 入口线路样式 */
.entrance-url-row {
  position: relative;
}

.entrance-copy-btn {
  font-size: 11px;
  height: 32px;
  border-radius: 16px;
  min-width: 90px;
  transition: all 0.3s ease;
}

.entrance-copy-btn:hover:not(.v-btn--disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(255, 87, 34, 0.2);
}

.entrance-copy-btn:disabled {
  opacity: 0.6;
}

/* 线路选择器包装样式 */
.line-selector-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* 邀请码容器样式 */
.invitation-code-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

/* 新增：水平布局的邀请码容器样式 */
.invitation-code-container-horizontal {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1;
  min-width: 0; /* 允许收缩 */
}

/* 新增：邀请码和特权标签的水平容器 */
.invitation-code-with-tag {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

/* 特权码标签样式 */
.privilege-tag {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  font-size: 10px;
  color: #FF8F00;
  font-weight: 500;
  box-shadow: none !important;
  filter: none !important;
  backdrop-filter: none !important;
}

/* 新增：水平布局的特权码标签样式 */
.privilege-tag-horizontal {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 193, 7, 0.08) 100%);
  border: 1px solid rgba(255, 193, 7, 0.4);
  border-radius: 10px;
  font-size: 9px;
  color: #FF8F00;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: none !important;
  filter: none !important;
  backdrop-filter: none !important;
}

/* 确保特权标签不继承任何阴影效果 */
.privilege-tag-horizontal *,
.privilege-tag * {
  box-shadow: none !important;
  filter: none !important;
}

/* 特权标签内的图标样式 */
.privilege-tag-horizontal .v-icon,
.privilege-tag .v-icon {
  box-shadow: none !important;
  filter: none !important;
  text-shadow: none !important;
}

.privilege-text {
  font-size: 10px;
  line-height: 1;
  text-shadow: none !important;
}

/* 邀请码行样式 */
.invitation-code-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

/* 新增：水平布局的邀请码行样式 */
.invitation-code-row-horizontal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.invitation-code-row:last-child {
  border-bottom: none;
}

.invitation-code-row-horizontal:last-child {
  border-bottom: none;
}

/* 兑换按钮样式 */
.redeem-button {
  flex-shrink: 0;
  font-size: 12px;
  height: 28px;
  border-radius: 14px;
  transition: all 0.3s ease;
}

/* 新增：水平布局的兑换按钮样式 */
.redeem-button-horizontal {
  flex-shrink: 0;
  font-size: 11px;
  height: 32px;
  border-radius: 16px;
  transition: all 0.3s ease;
  min-width: 90px;
}

.redeem-button:hover,
.redeem-button-horizontal:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.redeem-button:disabled,
.redeem-button-horizontal:disabled {
  opacity: 0.6;
}

/* 捐赠管理按钮样式 */
.donation-manage-btn {
  transition: all 0.3s ease;
}

.donation-manage-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
}

/* 积分商城按钮样式 */
.credits-store-btn {
  transition: all 0.3s ease;
}

.credits-store-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 167, 38, 0.3);
}

/* 个人活动数据卡片样式 */
.activity-section {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
}

.section-header-with-action {
  justify-content: space-between;
}

.list-toggle-btn {
  min-width: 76px;
}

.section-title {
  font-weight: 600;
  font-size: 16px;
  color: #333;
}

.activity-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  font-size: 14px;
  color: #666;
}

.activity-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.treasure-stats-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.stats-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.stats-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.stats-card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 14px;
  color: #555;
}

.stats-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.stat-label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.stat-value {
  font-weight: 700;
  font-size: 15px;
  padding: 2px 8px;
  border-radius: 6px;
  min-width: 50px;
  text-align: center;
}

.today-value {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%);
  color: #F57C00;
  border: 1px solid rgba(255, 152, 0, 0.2);
}

.week-value {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%);
  color: #1976D2;
  border: 1px solid rgba(33, 150, 243, 0.2);
}

.total-value {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%);
  color: #388E3C;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.treasure-value {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.14) 0%, rgba(255, 193, 7, 0.06) 100%);
  color: #F57C00;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.treasure-cost-value {
  background: linear-gradient(135deg, rgba(239, 83, 80, 0.14) 0%, rgba(239, 83, 80, 0.06) 100%);
  color: #C62828;
  border: 1px solid rgba(239, 83, 80, 0.3);
}

.treasure-win-value {
  background: linear-gradient(135deg, rgba(67, 160, 71, 0.14) 0%, rgba(67, 160, 71, 0.06) 100%);
  color: #2E7D32;
  border: 1px solid rgba(67, 160, 71, 0.3);
}

.blackjack-value {
  background: linear-gradient(135deg, rgba(233, 30, 99, 0.12) 0%, rgba(233, 30, 99, 0.05) 100%);
  color: #C2185B;
  border: 1px solid rgba(233, 30, 99, 0.28);
}

/* 决策准确率是唯一纯技巧口径，用更实的底色与主榜地位相称 */
.blackjack-accuracy-value {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.16) 0%, rgba(156, 39, 176, 0.07) 100%);
  color: #7B1FA2;
  border: 1px solid rgba(156, 39, 176, 0.32);
}

.blackjack-win-value {
  background: linear-gradient(135deg, rgba(67, 160, 71, 0.14) 0%, rgba(67, 160, 71, 0.06) 100%);
  color: #2E7D32;
  border: 1px solid rgba(67, 160, 71, 0.3);
}

.blackjack-jackpot-value {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.16) 0%, rgba(255, 193, 7, 0.06) 100%);
  color: #F57C00;
  border: 1px solid rgba(255, 193, 7, 0.32);
}

.stats-card-note {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed rgba(0, 0, 0, 0.08);
  font-size: 12px;
  color: #888;
  line-height: 1.5;
}

.stat-value.positive {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%) !important;
  color: #2E7D32 !important;
  border-color: rgba(76, 175, 80, 0.3) !important;
}

.stat-value.negative {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.08) 100%) !important;
  color: #C62828 !important;
  border-color: rgba(244, 67, 54, 0.3) !important;
}

/* 最近游戏记录样式 */
.recent-games-section {
  margin-top: 16px;
}

.recent-games-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recent-game-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: linear-gradient(135deg, rgba(245, 245, 245, 0.6) 0%, rgba(250, 250, 250, 0.8) 100%);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.recent-game-item:hover {
  background: linear-gradient(135deg, rgba(240, 240, 240, 0.8) 0%, rgba(248, 248, 248, 0.9) 100%);
  transform: translateX(2px);
}

.game-result {
  flex: 2;
}

.game-chip {
  font-size: 12px !important;
  height: 24px !important;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.game-change {
  flex: 1;
  text-align: center;
}

.change-value {
  font-weight: 600;
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 4px;
}

.change-value.positive {
  color: #2E7D32;
  background: rgba(76, 175, 80, 0.1);
}

.change-value.negative {
  color: #C62828;
  background: rgba(244, 67, 54, 0.1);
}

.game-date {
  flex: 1;
  text-align: right;
  font-size: 12px;
  color: #888;
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .activity-stats-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .section-header-with-action {
    align-items: flex-start;
    gap: 8px;
  }

  .list-toggle-btn {
    align-self: flex-end;
  }
  
  .stats-card {
    padding: 12px;
  }
  
  .stats-card-header {
    font-size: 13px;
  }
  
  .stat-value {
    font-size: 14px;
    min-width: 45px;
  }
  
  .recent-game-item {
    padding: 8px 10px;
  }
  
  .game-chip {
    font-size: 11px !important;
    height: 22px !important;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .change-value {
    font-size: 13px;
  }
  
  .game-date {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .activity-stats-grid {
    gap: 10px;
  }

  .section-header-with-action {
    flex-direction: column;
    align-items: flex-start;
  }

  .list-toggle-btn {
    align-self: flex-start;
    padding-left: 0;
  }
  
  .stats-card {
    padding: 10px;
  }
  
  .stat-item {
    gap: 8px;
  }
  
  .stat-label {
    font-size: 12px;
  }
  
  .stat-value {
    font-size: 13px;
    min-width: 40px;
    padding: 1px 6px;
  }
  
  .recent-game-item {
    padding: 8px 6px;
    gap: 4px;
  }
  
  .game-result {
    flex: 1.5;
    min-width: 0;
  }
  
  .game-chip {
    font-size: 10px !important;
    height: 20px !important;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .game-change {
    flex: 1;
    text-align: center;
    min-width: 0;
  }
  
  .change-value {
    font-size: 12px;
    padding: 1px 4px;
  }
  
  .game-date {
    flex: 1;
    text-align: right;
    font-size: 10px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* 大预言家最近结算记录：移动端改为两行布局，避免拥挤 */
  .prediction-recent-list .recent-game-item {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "result result"
      "change date";
    align-items: center;
    gap: 6px;
    padding: 10px 8px;
  }

  .prediction-recent-list .game-result {
    grid-area: result;
    min-width: 0;
  }

  .prediction-recent-list .game-change {
    grid-area: change;
    justify-self: start;
    text-align: left;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 0;
  }

  .prediction-recent-list .game-date {
    grid-area: date;
    justify-self: end;
    text-align: right;
    white-space: nowrap;
  }

  .prediction-recent-list .game-chip {
    max-width: 100%;
  }
}

/* 超小屏幕优化 */
@media (max-width: 360px) {
  .recent-game-item {
    padding: 6px 4px;
    gap: 2px;
  }
  
  .game-result {
    flex: 1.2;
  }
  
  .game-chip {
    font-size: 9px !important;
    height: 18px !important;
    max-width: 100px;
    padding: 0 4px !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .change-value {
    font-size: 11px;
    padding: 1px 3px;
  }
  
  .game-date {
    font-size: 9px;
  }

  .prediction-recent-list .recent-game-item {
    grid-template-columns: 1fr;
    grid-template-areas:
      "result"
      "change"
      "date";
    gap: 4px;
    padding: 8px 6px;
  }

  .prediction-recent-list .game-date {
    justify-self: start;
    text-align: left;
  }
}

/* 特殊的卡片颜色变化 */
.today-stats {
  border-left: 4px solid #FF9800;
}

.week-stats {
  border-left: 4px solid #2196F3;
}

.total-stats {
  border-left: 4px solid #4CAF50;
}

.treasure-stats {
  border-left: 4px solid #FFB300;
}

/* 社区链接部分样式 */
.community-links-section {
  margin-bottom: 20px;
}

.community-links-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: none;
}

.community-links-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

.community-links-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.community-link-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.9) 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.community-link-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.community-link-item:hover::before {
  opacity: 1;
}

.community-link-item:hover {
  transform: translateX(4px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  border-color: rgba(102, 126, 234, 0.2);
}

.link-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.group-link {
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%);
}

.channel-link {
  background: linear-gradient(135deg, #FF9800 0%, #FFB74D 100%);
}

.community-link-item:hover .link-icon-wrapper {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.link-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.link-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.link-description {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  opacity: 0.9;
}

.community-link-item:hover .link-title {
  color: #667eea;
}

.community-link-item:hover .link-description {
  color: #555;
  opacity: 1;
}

.link-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.copy-link-btn {
  opacity: 0;
  transition: opacity 0.3s ease;
  background: rgba(102, 126, 234, 0.1) !important;
  border-radius: 6px;
}

.community-link-item:hover .copy-link-btn {
  opacity: 1;
}

.copy-link-btn:hover {
  background: rgba(102, 126, 234, 0.2) !important;
  transform: scale(1.1);
}

.no-links-message {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #999;
  font-size: 14px;
  font-style: italic;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .community-links-grid {
    gap: 10px;
  }
  
  .community-link-item {
    padding: 14px 16px;
    gap: 12px;
  }
  
  .link-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }
  
  .link-title {
    font-size: 14px;
  }
  
  .link-description {
    font-size: 12px;
  }

  .copy-link-btn {
    opacity: 1; /* 在移动设备上始终显示复制按钮 */
  }
}

@media (max-width: 480px) {
  .community-link-item {
    padding: 12px 14px;
    gap: 10px;
  }
  
  .link-icon-wrapper {
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }
  
  .link-icon-wrapper .v-icon {
    font-size: 18px !important;
  }
  
  .link-title {
    font-size: 13px;
  }
  
  .link-description {
    font-size: 11px;
  }

  .link-actions {
    gap: 4px;
  }

  .copy-link-btn {
    opacity: 1; /* 在小屏设备上始终显示 */
  }
}

/* Premium 按钮美化 */
.premium-button {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.5px;
  font-weight: 500;
  text-transform: none;
  min-width: 120px;
}

.premium-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.premium-button.premium-active {
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.premium-button.premium-active:hover {
  background: linear-gradient(135deg, #43A047 0%, #5CB85C 100%);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
}

.premium-button:not(.premium-active) {
  background: linear-gradient(135deg, #ECEFF1 0%, #CFD8DC 100%);
  color: #263238 !important;
  border: 1px solid rgba(69, 90, 100, 0.3);
  font-weight: 600;
}

.premium-button:not(.premium-active):hover {
  background: linear-gradient(135deg, #E0E7EA 0%, #B0BEC5 100%);
  color: #1A1A1A !important;
  border-color: rgba(69, 90, 100, 0.4);
}

.premium-button .v-icon {
  transition: transform 0.2s ease;
}

.premium-button:hover .v-icon {
  transform: scale(1.1);
}

.gap-2 {
  gap: 8px;
}
</style>
